import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { IconPlus, IconLoader, IconAlert, IconTrash } from "../components/Icons";

interface StockReceiptItem {
  product_id: string;
  product_name: string;
  quantity: number;
  unit_cost: number | null;
}

interface StockReceipt {
  id: string;
  receipt_number: string | null;
  status: string;
  movement_type: string;
  warehouse_name: string;
  source_warehouse_name: string | null;
  supplier_name: string | null;
  total_cost: number;
  received_at: string;
  note: string | null;
  items: StockReceiptItem[];
}

interface StockReceiptPage {
  items: StockReceipt[];
  total: number;
}

export default function StockReceipts() {
  const { user } = useCurrentUser();
  const canReceipts = can(user, "stock_receipts");
  const canTransfers = can(user, "stock_transfers");
  const canCorrections = can(user, "stock_corrections");
  const typeAllowed = (mt: string) =>
    mt === "transfer" ? canTransfers : mt === "correction" ? canCorrections : canReceipts;
  const { t } = useTranslation();
  const [items, setItems] = useState<StockReceipt[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      limit: String(pageSize),
      offset: String(pageIndex * pageSize),
    });
    api.get<StockReceiptPage>(`/stock-receipts?${params}`)
      .then(({ data }) => { setItems(data.items); setTotal(data.total); })
      .catch(() => setErr(t("stockReceipt.loadError")))
      .finally(() => setLoading(false));
  }, [pageSize, pageIndex, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPageIndex(0); }, [pageSize]);

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  async function remove(r: StockReceipt) {
    if (!confirm(t("stockReceipt.deleteConfirm"))) return;
    try {
      await api.delete(`/stock-receipts/${r.id}`);
      load();
    } catch (e: any) {
      setErr(apiError(e, t("stockReceipt.saveError")));
    }
  }

  if (loading && items.length === 0) return <div style={{ display: "flex", justifyContent: "center", padding: 80 }}><IconLoader size={32} className="muted" /></div>;

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h1>{t("stockReceipt.historyTitle")}</h1>
          <p>{t("stockReceipt.historySubtitle")}</p>
        </div>
        {(canReceipts || canTransfers) && (
        <Link to="/stock-receipts/new" className="btn btn-primary">
          <IconPlus size={18} /> {t("stockReceipt.new")}
        </Link>
        )}
      </div>

      {err && <div className="card" style={{ padding: 12, marginBottom: 20, background: "var(--red-dim)", color: "var(--red)", display: "flex", alignItems: "center", gap: 8 }}><IconAlert size={16} /> {err}</div>}

      {items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48, color: "var(--muted)" }}>{t("stockReceipt.noReceipts")}</div>
      ) : (
        <div className="card" style={{ overflow: "auto", opacity: loading ? 0.6 : 1 }}>
          <table>
            <thead>
              <tr>
                <th>{t("stockReceipt.date")}</th>
                <th>{t("stockReceipt.type")}</th>
                <th>{t("stockReceipt.document")}</th>
                <th>{t("stockReceipt.warehouse")}</th>
                <th>{t("stockReceipt.source")}</th>
                <th>{t("stockReceipt.supplierName")}</th>
                <th>{t("stockReceipt.itemCount")}</th>
                <th>{t("stockReceipt.totalAmount")}</th>
                <th>{t("common.status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.filter((r) => typeAllowed(r.movement_type)).map((r) => (
                <tr key={r.id}>
                  <td>{new Date(r.received_at).toLocaleDateString("cs-CZ")}</td>
                  <td>{t(`stockReceipt.${r.movement_type}` as any) || r.movement_type}</td>
                  <td>{r.receipt_number || "—"}</td>
                  <td>{r.warehouse_name}</td>
                  <td>{r.source_warehouse_name || "—"}</td>
                  <td>{r.supplier_name || "—"}</td>
                  <td>{r.items.length}</td>
                  <td>{(r.total_cost / 100).toFixed(2)} Kč</td>
                  <td>
                    <span style={{
                      padding: "3px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: r.status === "draft" ? "var(--accent-dim)" : "var(--green-dim)",
                      color: r.status === "draft" ? "var(--accent)" : "var(--green)",
                    }}>{t(`stockReceipt.status.${r.status}` as any)}</span>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {typeAllowed(r.movement_type) && (
                    <Link to={`/stock-receipts/${r.id}`} className="ghost" style={{ fontSize: 13 }}>
                      {t("stockReceipt.detail")}
                    </Link>
                    )}
                    {r.status === "draft" && (
                      <>
                        {typeAllowed(r.movement_type) && (
                        <Link to={`/stock-receipts/${r.id}/edit`} className="ghost" style={{ fontSize: 13 }}>
                          {t("stockReceipt.continue")}
                        </Link>
                        )}
                        {typeAllowed(r.movement_type) && (
                        <button className="ghost" onClick={() => remove(r)} style={{ color: "var(--red)" }} title={t("common.delete")}>
                          <IconTrash size={16} />
                        </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "12px 16px", borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--muted)" }}>
              <span>{t("stockReceipt.perPage")}</span>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ width: "auto" }}>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>· {t("stockReceipt.total", { total })}</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <button className="ghost" disabled={pageIndex === 0} onClick={() => setPageIndex(i => i - 1)}>‹ {t("stockReceipt.prev")}</button>
              <span style={{ color: "var(--muted)" }}>{pageIndex + 1} / {pageCount}</span>
              <button className="ghost" disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex(i => i + 1)}>{t("stockReceipt.next")} ›</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
